import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Lock, Gift, Users, PackageCheck } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CountdownTimer } from "@/components/CountdownTimer";

export const Showcase = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/listings").then(({ data }) => setItems(data.slice(0, 8))).catch(() => {});
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-accent/10 border border-accent/30 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-accent">
            <Gift className="h-3.5 w-3.5" /> Disponibili ora — gratis
          </div>
          <h2 className="font-head font-extrabold text-3xl sm:text-4xl tracking-tight mt-4">Regali che ti aspettano</h2>
          <p className="text-muted-foreground mt-2 max-w-md">Questi oggetti sono gratis nella community. Iscriviti per richiederli prima che spariscano.</p>
        </div>
        {stats && (
          <div className="flex gap-6">
            <div className="text-center"><div className="font-head font-extrabold text-2xl text-primary flex items-center gap-1"><Gift className="h-5 w-5" />{stats.items}</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Disponibili</div></div>
            <div className="text-center"><div className="font-head font-extrabold text-2xl text-primary flex items-center gap-1"><PackageCheck className="h-5 w-5" />{stats.gifted}</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Regalati</div></div>
            <div className="text-center"><div className="font-head font-extrabold text-2xl text-primary flex items-center gap-1"><Users className="h-5 w-5" />{stats.members}</div><div className="text-xs text-muted-foreground uppercase tracking-wider">Membri</div></div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {items.map((item, i) => (
          <motion.button
            key={item.id}
            onClick={() => navigate("/register")}
            data-testid={`showcase-item-${item.id}`}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
            className="group text-left rounded-2xl bg-card border border-border/60 tactile-shadow overflow-hidden hover:-translate-y-1 transition-transform"
          >
            <div className="aspect-[4/3] bg-secondary overflow-hidden relative">
              <img src={mediaUrl(item.images?.[0])} alt={item.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <Badge className="absolute top-3 left-3 bg-white/90 text-foreground hover:bg-white border-0 font-medium">{item.category}</Badge>
              {item.expires_at && <CountdownTimer expiresAt={item.expires_at} className="absolute bottom-3 left-3 shadow-sm z-10" />}
              <div className="absolute inset-0 bg-primary/0 group-hover:bg-primary/60 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <span className="flex items-center gap-2 text-white font-semibold text-sm bg-black/30 px-4 py-2 rounded-full backdrop-blur-sm">
                  <Lock className="h-4 w-4" /> Iscriviti per richiederlo
                </span>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-head font-bold text-lg leading-tight line-clamp-1">{item.title}</h3>
              <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> {item.city}{item.province ? ` (${item.province})` : ""}</div>
            </div>
          </motion.button>
        ))}
      </div>

      <div className="text-center mt-10">
        <Button size="lg" onClick={() => navigate("/register")} data-testid="showcase-cta" className="rounded-full h-12 px-8 text-base">
          Iscriviti gratis e richiedi
        </Button>
        <p className="text-xs text-muted-foreground mt-3">Ricevi 5 crediti di benvenuto · Nessun costo per iscriverti</p>
      </div>
    </section>
  );
};

export default Showcase;
